import React, { useState, useRef, useEffect } from "react";
import "./WeeklyTimlineScheduler.css"; // Make sure this path is correct
import { format, startOfWeek, addDays } from 'date-fns';
import { enGB, srLatn } from "date-fns/locale";
import {
    Modal,
    ModalBody,
    FormGroup,
    Label,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
} from "reactstrap";
import { IoIosInformationCircleOutline } from "react-icons/io";
import { useLocation } from "react-router-dom";
import apiRequest from '../../api';

// Sample data for demonstration (matching the image)
const sampleItems = [
    {
        id: "1",
        start_time: "2025-03-10T10:00:00",
        end_time: "2025-03-10T11:00:00",
        username: "User1",
        name: "",
        seats: "15",
        notes: ""
    },
    {
        id: "2",
        start_time: "2025-03-10T11:30:00",
        end_time: "2025-03-10T12:30:00",
        username: "User2",
        name: "",
        seats: "15",
        notes: ""
    },
    {
        id: "3",
        start_time: "2025-03-10T14:00:00",
        end_time: "2025-03-10T15:00:00",
        username: "User3",
        name: "",
        seats: "15",
        notes: ""
    },
    {
        id: "4",
        start_time: "2025-03-11T11:00:00",
        end_time: "2025-03-11T12:00:00",
        username: "User4",
        name: "",
        seats: "15",
        notes: ""
    },
    {
        id: "6",
        start_time: "2025-03-11T09:00:00",
        end_time: "2025-03-11T10:00:00",
        username: "User6",
        name: "",
        seats: "15",
        notes: ""
    },
    {
        id: "7",
        start_time: "2025-03-11T13:45:00",
        end_time: "2025-03-11T14:45:00",
        username: "Marko",
        name: "Marko",
        seats: "15",
        notes: ""
    },
    {
        id: "8",
        start_time: "2025-03-12T11:30:00",
        end_time: "2025-03-12T12:30:00",
        username: "User8eeeeeeeeeeeeeeeeeeeee",
        name: "",
        seats: "15",
        notes: ""
    },
    {
        id: "9",
        start_time: "2025-03-13T11:00:00",
        end_time: "2025-03-13T12:00:00",
        username: "User9",
        name: "",
        seats: "15",
        notes: ""
    },
    {
        id: "10",
        start_time: "2025-03-13T14:00:00",
        end_time: "2025-03-13T15:00:00",
        username: "User10",
        name: "",
        seats: "15",
        notes: ""
    },
    {
        id: "11",
        start_time: "2025-03-14T11:30:00",
        end_time: "2025-03-14T13:15:00",
        username: "User11",
        name: "",
        seats: "15",
        notes: ""
    },
    {
        id: "12",
        start_time: "2025-03-14T13:30:00",
        end_time: "2025-03-14T18:00:00",
        username: "User12",
        name: "",
        seats: "15",
        notes: ""
    },
];

// Helper function to convert time to minutes from midnight
const timeToMinutes = (time) => {
    const date = new Date(time);
    return date.getHours() * 60 + date.getMinutes();
};
// Generate days of the week
const generateWeekDays = (selectedDate, selectedLanguageRef) => {
    const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 }); // Start from Monday
    const days = [];

    for (let i = 0; i < 7; i++) {
        const day = addDays(weekStart, i);
        days.push({
            date: day,
            dayName: format(day, 'EEE', { locale: selectedLanguageRef.current === 'RS' ? srLatn : enGB }).toUpperCase(),
            dayNumber: format(day, 'd')
        });
    }

    return days;
};
const formatTime = (date) => {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
};


const WeeklyTimelineScheduler = ({ onEdit, fetchReservations, items = [], selectedDate }) => {

    const selectedLanguageRef = useRef(localStorage.getItem('Language') || 'EN');
    const [selectedItem, setSelectedItem] = useState(null);
    const [infoModal, setInfoModal] = useState(false);
    const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, itemId: null });
    const [weekDays, setWeekDays] = useState(generateWeekDays(selectedDate, selectedLanguageRef));
    const mainBodyRef = useRef(null);
    const [mainBodyWidth, setMainBodyWidth] = useState(0);
    const [longPressTimer, setLongPressTimer] = useState(null);
    const [isMobile, setIsMobile] = useState(false);
    const { pathname } = useLocation()

    // Detect if device is mobile
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.matchMedia('(max-width: 768px), (pointer: coarse)').matches);
        };

        checkMobile(); // Check on initial load
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        // Initialize weekDays when component mounts or selectedDate changes
        setWeekDays(generateWeekDays(selectedDate, selectedLanguageRef));

        // Set up language change listener
        const handleLanguageChange = (event) => {
            selectedLanguageRef.current = event.detail['selectedLanguage'];
            setWeekDays(generateWeekDays(selectedDate, selectedLanguageRef));
        };

        window.addEventListener('languageChange', handleLanguageChange);

        // Clean up event listener when component unmounts or selectedDate changes
        return () => {
            window.removeEventListener('languageChange', handleLanguageChange);
        };
    }, [selectedDate]);

    useEffect(() => {
        const handleClick = () => {
            if (contextMenu.visible) {
                hideContextMenu();
            }
        };
        const handleScroll = (event) => {
            // Hide context menu when scrolling
            if (contextMenu.visible) {
                setContextMenu(prev => ({ ...prev, visible: false }));
            }
        };

        const parentContainer = mainBodyRef.current?.closest('.body');

        if (parentContainer) {
            parentContainer.addEventListener('scroll', handleScroll);
            window.addEventListener('scroll', handleScroll);
        }
        document.addEventListener('click', handleClick);

        return () => {
            document.removeEventListener('click', handleClick);
            if (parentContainer) {
                parentContainer.removeEventListener('scroll', handleScroll);
                window.removeEventListener('scroll', handleScroll);
            }
        };
    }, [contextMenu.visible]);


    // Add effect to measure and update the mainBodyWidth
    useEffect(() => {
        const updateWidth = () => {
            if (mainBodyRef.current) {
                setMainBodyWidth(mainBodyRef.current.offsetWidth);
            }
        };

        // Initial measurement
        updateWidth();

        // Add event listener for resize
        window.addEventListener('resize', updateWidth);

        // Create a MutationObserver to watch for changes in the DOM that might affect width
        const observer = new MutationObserver(updateWidth);

        if (mainBodyRef.current) {
            observer.observe(mainBodyRef.current.parentNode, {
                attributes: true,
                childList: true,
                subtree: true
            });
        }

        // Clean up
        return () => {
            window.removeEventListener('resize', updateWidth);
            observer.disconnect();
        };
    }, []);


    // Time configuration - Updated to go until 18:00
    const startHour = 8; // 8:00 AM
    const endHour = 18; // 6:00 PM
    const timelineHours = [];
    for (let hour = startHour; hour <= endHour; hour++) {
        timelineHours.push(hour);
    }

    const startOfDay = startHour * 60; // minutes from midnight
    const endOfDay = endHour * 60; // minutes from midnight
    const hourHeight = 60; // Height per hour

    const timelineWidth = window.innerWidth <= 768 ? 40 : 100; // Width of the time column
    const totalContentHeight = (endHour - startHour + 1) * hourHeight;

    const dayColumnWidth = `${100 / 7}%`;

    const toggleInfoModal = (item) => {
        setInfoModal(!infoModal);
        if (!infoModal) {
            setSelectedItem(item);
        } else {
            setSelectedItem(null);
        }
    };
    const toggleModal = (item) => {
        onEdit(item);
    };

    // Function to handle traditional right-click context menu
    const handleContextMenu = (item, event) => {
        event.preventDefault(); // Prevent default context menu
        event.stopPropagation();

        showContextMenuAt(item, event.clientX, event.clientY);
    };

    // Function to handle long-press (for mobile)
    const handleTouchStart = (item, event) => {
        // Start a timer for long press
        const timer = setTimeout(() => {
            // Get touch position
            const touch = event.touches[0];

            // Show context menu at touch position
            showContextMenuAt(item, touch.clientX, touch.clientY);

            // Provide haptic feedback if available
            if (navigator.vibrate) {
                navigator.vibrate(50);
            }
        }, 500); // 500ms long press

        setLongPressTimer(timer);
    };

    const handleTouchEnd = () => {
        // Clear the timer if touch ends before long press is triggered
        if (longPressTimer) {
            clearTimeout(longPressTimer);
            setLongPressTimer(null);
        }
    };

    const handleTouchMove = () => {
        // Clear the timer if user moves finger during press
        if (longPressTimer) {
            clearTimeout(longPressTimer);
            setLongPressTimer(null);
        }
    };

    // Helper function to show context menu at specified coordinates
    const showContextMenuAt = (item, x, y) => {
        setContextMenu({
            visible: true,
            x: x,
            y: y,
            itemId: item.id
        });
    };

    // Add this function to hide the context menu
    const hideContextMenu = () => {
        setContextMenu({ visible: false, x: 0, y: 0, itemId: null });
    };

    const onDelete = async (item) => {
        try {
            const token = sessionStorage.getItem("jwt");
            const reservationCode = String(item.id).replace(/"/g, '');
            await apiRequest(`/api/reservations/deleteReservation?reservationCode=${reservationCode}`, 'POST', null, token);
            fetchReservations();
        } catch (error) {
            try {
                // Parse the error message as JSON
                const errorData = JSON.parse(error.message);

                // Extract and display the error message from the Body
                if (errorData.Body && errorData.Body.error) {
                    window.alert(errorData.Body.error); // Display the error message
                }
            } catch (parseError) {
                // Handle cases where the error message is not valid JSON
                console.error("Failed to parse error message:", parseError);
            }
        }
    };

    items = items.length > 0 ? items : sampleItems;


    return (
        <React.Fragment>
            <div className='wrapper' style={{ maxHeight: `min(${totalContentHeight + 65}px, calc(100vh - 250px))` }}>
                {/* Header with days of the week */}
                <div className="header">
                    {/* Time column header */}
                    <div className="time-header"
                        style={{
                            width: `${timelineWidth}px`,
                        }}></div>

                    {/* Days of week headers - Now using the same structure as the grid below */}
                    <div style={{
                        display: "flex",
                        width: `${mainBodyWidth}px`,
                        overflowX: "hidden",
                    }}>
                        {weekDays.map((day, index) => (
                            <div
                                key={index}
                                className="date-day-header"
                                style={{
                                    width: dayColumnWidth,
                                }}
                            >
                                <div className="date-header">{day.dayName}</div>
                                <div className="day-header">
                                    {day.dayNumber}
                                </div>
                                {/* Border div positioned to start from 1/3 of the height */}
                                <div
                                    style={{
                                        position: "absolute",
                                        left: 0,
                                        bottom: 0,
                                        height: "25%", // Covers 2/3 of the height from the bottom
                                        borderLeft: "1px solid #e0e0e0",
                                    }}
                                ></div>
                            </div>
                        ))}
                    </div>
                    {/* This additional div mimics the scrollbar width to ensure alignment */}
                    <div style={{ display: "flex", flexShrink: 0, flex: "1" }}></div>
                </div>

                {/* Main scheduler content */}
                <div className="body" style={{
                    height: "calc(100% - 65px)"
                }}
                >
                    {/* Time column */}
                    <div className="time-body" style={{
                        width: `${timelineWidth}px`,
                    }}>
                        {timelineHours.map((hour, index) => (
                            <div
                                key={index}
                                className="time"
                                style={{
                                    height: `${hourHeight}px`,
                                }}
                            >
                                {`${hour}:00`}
                            </div>
                        ))}
                    </div>

                    {/* Scheduler grid */}
                    <div
                        className="main-body"
                        ref={mainBodyRef}
                        style={{
                            height: totalContentHeight,
                        }}
                    >
                        {/* Days columns */}
                        {weekDays.map((day, dayIndex) => (
                            <div
                                key={dayIndex}

                                style={{
                                    width: dayColumnWidth,
                                    position: "relative",
                                    borderLeft: "1px solid #e0e0e0",
                                    height: "100%"
                                }}
                            >
                                {/* Appointments for this day */}
                                {items.filter(item => {
                                    const itemDate = new Date(item.start_time);
                                    return format(itemDate, 'yyyy-MM-dd') === format(day.date, 'yyyy-MM-dd');
                                }).map((item) => {
                                    const start = timeToMinutes(item.start_time);
                                    const end = timeToMinutes(item.end_time);

                                    // Calculate position
                                    const top = (start - startOfDay) * hourHeight / 60;
                                    const height = (end - start) * hourHeight / 60;

                                    return (
                                        <div key={item.id}
                                            className="slot-wrapper"
                                            style={{
                                                top: `${top + 32}px`,
                                            }}
                                        >
                                            <div
                                                className="slot"
                                                style={{
                                                    height: `${height}px`,
                                                    width: "90%",
                                                    background: (!pathname.includes('admin') && item.username !== "") ? "rgba(5, 67, 68, 0.85)" : "rgba(137, 168, 168, 0.77)",
                                                    pointerEvents: (!pathname.includes('admin') && item.username === "") ? "none" : "auto", // Disable interactions
                                                    cursor: (!pathname.includes('admin') && item.username === "") ? "not-allowed" : "pointer", // Show "not-allowed" cursor
                                                }}
                                                onClick={() => {
                                                    if (pathname.includes('admin') || item.username !== "") {
                                                        toggleInfoModal(item);
                                                    }
                                                }}
                                                onContextMenu={(e) => {
                                                    if (pathname.includes('admin') || item.username !== "") {
                                                        handleContextMenu(item, e);
                                                    }
                                                }}
                                                onTouchStart={(e) => {
                                                    if (pathname.includes('admin') || item.username !== "") {
                                                        handleTouchStart(item, e);
                                                    }
                                                }}
                                                onTouchEnd={(e) => {
                                                    if (pathname.includes('admin') || item.username !== "") {
                                                        handleTouchEnd(e);
                                                    }
                                                }}
                                                onTouchMove={(e) => {
                                                    if (pathname.includes('admin') || item.username !== "") {
                                                        handleTouchMove(e);
                                                    }
                                                }}
                                                onTouchCancel={(e) => {
                                                    if (pathname.includes('admin') || item.username !== "") {
                                                        handleTouchEnd(e);
                                                    }
                                                }}
                                            >
                                                <div
                                                    className="slot-text"
                                                >
                                                    <div>{item.username}</div>
                                                    <div>
                                                        {formatTime(new Date(item.start_time))} - {formatTime(new Date(item.end_time))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}

                        {/* Inside your render method, right after the closing </div> of your scheduler-container */}
                        {contextMenu.visible && (
                            <div
                                className="dropdown-container"
                                style={{
                                    position: 'fixed',
                                    left: `${contextMenu.x}px`,
                                    top: `${contextMenu.y}px`,
                                    transform: isMobile ? 'translateX(-50%)' : 'none', // Center horizontally on mobile
                                }}
                            >
                                <div
                                    className="dropdown-button"
                                    onClick={() => {
                                        const item = items.find(i => i.id === contextMenu.itemId);
                                        if (item) toggleModal(item);
                                        hideContextMenu();
                                    }}
                                >
                                    {selectedLanguageRef.current === 'EN' ? "Edit" : "Izmeni"}
                                </div>

                                <div
                                    className="mx-3"
                                    style={{
                                        borderTop: 'solid 1px #FFF'
                                    }}
                                />

                                <div
                                    className="dropdown-button"
                                    onClick={() => {
                                        const item = items.find(i => i.id === contextMenu.itemId);
                                        if (item) onDelete(item);
                                        hideContextMenu();
                                    }}
                                >
                                    {selectedLanguageRef.current === 'EN' ? "Delete" : "Ukloni"}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <Modal
                isOpen={infoModal}
                toggle={toggleInfoModal}
                centered
                className="custom-model"
                backdrop="static"
                keyboard={false}
            >
                <ModalBody className="p-3">
                    <div className="row justify-content-end ">
                        <div className="col-md-2 d-flex justify-content-end pt-2 ">
                            <button type="button" className="btn-close" onClick={() => setInfoModal(false)} />
                        </div>
                    </div>
                    {selectedItem ? (
                        <>
                            <div className="row justify-content-center ">
                                <div className="col-md-8">
                                    <FormGroup>
                                        <Label className=" label fw-bold">{selectedLanguageRef.current === 'EN' ? "User:" : "Korisnik:"}</Label>
                                        <Label className=" label ms-2 ">{selectedItem.username}</Label>
                                    </FormGroup>
                                </div>
                            </div>

                            <div className="row justify-content-center mt-2">
                                <div className="col-md-8">
                                    <FormGroup>
                                        <Label className="fw-bold label ">{selectedLanguageRef.current === 'EN' ? "Date:" : "Datum:"}</Label>
                                        <span className="ms-2 label">{format(selectedItem.start_time, "dd.MM.yyyy")}</span>
                                    </FormGroup>
                                </div>
                            </div>

                            <div className="row justify-content-center mt-2">
                                <div className="col-md-8">
                                    <FormGroup>
                                        <Label className="fw-bold label">{selectedLanguageRef.current === 'EN' ? "Time:" : "Vreme:"}</Label>
                                        <span className="ms-2 label">
                                            {selectedItem.start_time && selectedItem.end_time
                                                ? `${formatTime(new Date(selectedItem.start_time))} - ${formatTime(new Date(selectedItem.end_time))}`
                                                : "N/A"}
                                        </span>
                                    </FormGroup>
                                </div>
                            </div>

                            <div className="row justify-content-center mt-2">
                                <div className="col-md-8">
                                    <FormGroup>
                                        <Label className="fw-bold label ">{selectedLanguageRef.current === 'EN' ? "Name:" : "Ime:"}</Label>
                                        <span className="ms-2 label">{selectedItem.name}</span>
                                    </FormGroup>
                                </div>
                            </div>
                            
                            <div className="row justify-content-center mt-2">
                                <div className="col-md-8">
                                    <FormGroup>
                                        <Label className="fw-bold label ">{selectedLanguageRef.current === 'EN' ? "Arrangement:" : "Postavka:"}</Label>
                                        <span className="ms-2 label">{selectedItem.seats}</span>
                                    </FormGroup>
                                </div>
                            </div>
                           
                            {selectedItem.notes != "" && (
                                <div className="row justify-content-center mt-2">
                                    <div className="col-md-8">
                                        <FormGroup>
                                            <Label className="fw-bold label ">{selectedLanguageRef.current === 'EN' ? "Note:" : "Napomena:"}</Label>
                                            <span className="ms-2 label">{selectedItem.notes}</span>
                                        </FormGroup>
                                    </div>
                                </div>
                            )}
                        </>) : (

                        <div></div>
                    )
                    }
                </ModalBody>
            </Modal>
        </React.Fragment>
    );
}

export default WeeklyTimelineScheduler;